import * as React from "react";
import { Loading } from "@skve/components";
import type { PiletEntries } from "piral-core";

import { UserProvider, UserEntity } from "@/providers/UserProvider";
import { loadInitial } from "@/api";

const Applet = React.lazy(() => import("@/modes/Applet"));
const Portal = React.lazy(() => import("@/modes/Portal"));
const Guest = React.lazy(() => import("@/modes/Guest"));
const Interrupted = React.lazy(() => import("@/modes/Interrupted"));

const defaultInitialState = {
  loaded: false,
  pilets: [] as PiletEntries,
  user: undefined as UserEntity | undefined,
  error: undefined as unknown,
  app: undefined as string | undefined,
  params: undefined as any,
};

function useInitialState(data?: string) {
  const [initialState, setInitialState] = React.useState(defaultInitialState);

  React.useEffect(() => {
    loadInitial(data).then(
      ({ pilets, user, app, params }) =>
        setInitialState({
          loaded: true,
          pilets,
          user,
          error: undefined,
          app,
          params,
        }),
      (error) => {
        console.error("Could not load the initial state", error);
        setInitialState({ ...defaultInitialState, loaded: true, error });
      },
    );
  }, []);

  return initialState;
}

const FullApp: React.FC = () => {
  const state = useInitialState();

  if (state.user) {
    return (
      <UserProvider.Provider value={state.user}>
        <Portal pilets={state.pilets} />
      </UserProvider.Provider>
    );
  }

  if (!state.loaded) {
    return <Loading />;
  }

  if (state.error) {
    return <Interrupted error={state.error} />;
  }

  return <Guest />;
};

const MicroApp: React.FC = () => {
  const [, , app, ...segs] = location.pathname.split("/");
  const path = segs.join("/");
  const hash = location.hash.substring(1);
  const query = location.search.substring(1);
  const state = useInitialState(
    encodeURIComponent(
      JSON.stringify({
        app,
        path,
        hash,
        query,
      }),
    ),
  );

  if (!state.loaded) {
    return <Loading />;
  }

  if (state.error) {
    return <Interrupted error={state.error} />;
  }

  return (
    <Applet pilets={state.pilets} app={state.app!} params={state.params!} />
  );
};

export const App: React.FC = () => {
  if (location.pathname.startsWith("/app/")) {
    return <MicroApp />;
  } else {
    return <FullApp />;
  }
};
